import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  assignCaseDoctorApiV1AdminCasesCaseIdDoctorPatch,
  assignCaseReferrerApiV1AdminCasesCaseIdReferrerPatch,
  setCaseDoctorReadyApiV1AdminCasesCaseIdDoctorReadyPatch,
} from "@/api/generated/sdk.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

/**
 * Admin: assign (or clear) the referrer on a case (access levels v1). Passing
 * `referrerUserId: null` unassigns. Backend validates the target is a referrer.
 */
export const useAssignCaseReferrer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      caseId,
      referrerUserId,
    }: {
      caseId: string;
      referrerUserId: string | null;
    }) => {
      const response = await assignCaseReferrerApiV1AdminCasesCaseIdReferrerPatch({
        client: apiClient,
        path: { case_id: caseId },
        body: { referrer_user_id: referrerUserId },
      });
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to assign referrer: ${
            (response.error as { detail?: string })?.detail ||
            JSON.stringify(response.error)
          }`
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.CASES] });
    },
  });
};

/**
 * Admin: reassign the doctor who owns a case (MDRS-234).
 *
 * The exceptional path — mis-assignment or leave cover. Normal assignment happens
 * at creation, because blob paths are prefixed with the owner's email: files
 * already uploaded stay under the previous owner's prefix until they're moved.
 */
export const useAssignCaseDoctor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, userId }: { caseId: string; userId: string }) => {
      const response = await assignCaseDoctorApiV1AdminCasesCaseIdDoctorPatch({
        client: apiClient,
        path: { case_id: caseId },
        body: { user_id: userId },
      });
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to reassign doctor: ${
            (response.error as { detail?: string })?.detail ||
            JSON.stringify(response.error)
          }`
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.CASES] });
    },
  });
};

/**
 * Admin: flip a case's "Ready for doctor" gate (MDRS-230).
 *
 * Until the flag is on, the case is absent from the assigned doctor's list and
 * their direct reads 404.
 *
 * Pass `invalidate: false` when the caller already refreshes the cases cache
 * (e.g. UpdateCaseModal, which also runs `useUpdateCase`) — otherwise both
 * mutations invalidate the same `[CASES]` prefix and every mounted case query
 * refetches twice per save.
 */
export const useSetDoctorReady = ({ invalidate = true } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      caseId,
      isDoctorReady,
    }: {
      caseId: string;
      isDoctorReady: boolean;
    }) => {
      const response = await setCaseDoctorReadyApiV1AdminCasesCaseIdDoctorReadyPatch(
        {
          client: apiClient,
          path: { case_id: caseId },
          body: { is_doctor_ready: isDoctorReady },
        }
      );
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to update doctor-ready status: ${
            (response.error as { detail?: string })?.detail ||
            JSON.stringify(response.error)
          }`
        );
      }
      return response.data;
    },
    onSuccess: () => {
      if (invalidate) {
        queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.CASES] });
      }
    },
  });
};
